/**
 * +specs: the Advancement Trial cards (docs/design/classes/specs.md "The Advancement Trial"), drawn
 * into Warden Ilsa's panel: one card per specialisation of the hero's class with its role, tagline,
 * fantasy, signature mechanic and the evolved kit (key, icon, new name, the base ability it came
 * from). Built specs offer "Try (30 s)" and "Choose"; the rest show "Arrives soon". Pure DOM, no
 * per-frame work.
 */
import type { ClassDef, ContentRegistry, SpecDef } from '@keystone/sim';
import { specKit } from '@keystone/sim';
import { SPEC_INFO, type SpecInfo, specDef } from '@keystone/content';
import { el } from '../dom';
import { iconUrl } from '../icons';

export interface SpecCardsOpts {
  classId: string;
  /** The hero's current spec (null = none yet). */
  current: string | null;
  level: number;
  needLevel: number;
  gold: number;
  respecGold: number;
  content: ContentRegistry;
  onTry(specId: string): void;
  onChoose(specId: string): void;
}

const ROLE_LABEL: Readonly<Record<string, string>> = { tank: 'Tank', healer: 'Healer', dps: 'Damage' };

/** One evolved-kit row: key, icon, the spec ability's name, and "from <base>". */
function kitRow(parent: HTMLElement, key: string, abilityId: string, baseId: string | null, content: ContentRegistry): void {
  const row = el('div', 'fx-spec-kit-row', parent);
  el('span', 'fx-spec-key', row, key);
  const url = iconUrl(abilityId, 64);
  if (url) {
    const img = el('img', 'fx-spec-icon', row);
    img.src = url;
    img.alt = '';
  }
  const a = content.ability(abilityId);
  const name = el('span', 'fx-spec-ab', row, a?.name ?? abilityId);
  if (a?.description) name.title = a.description;
  if (baseId) el('span', 'fx-spec-from', row, `from ${content.ability(baseId)?.name ?? baseId}`);
  else el('span', 'fx-spec-from new', row, 'new');
}

function card(parent: HTMLElement, info: SpecInfo, def: SpecDef | undefined, base: ClassDef | undefined, o: SpecCardsOpts): void {
  const mine = o.current === info.id;
  const c = el('div', `fx-spec-card role-${info.role}${mine ? ' mine' : ''}${def ? '' : ' soon'}`, parent);
  c.style.setProperty('--spec-tint', info.ui.tint);
  c.style.setProperty('--spec-glow', info.ui.glow);
  const head = el('div', 'fx-spec-head', c);
  el('div', 'fx-spec-name', head, info.name);
  el('div', `fx-spec-role role-${info.role}`, head, ROLE_LABEL[info.role] ?? info.role);
  el('div', 'fx-spec-tag', c, info.ui.tagline);
  el('div', 'fx-spec-fantasy', c, info.ui.fantasy);
  const sig = el('div', 'fx-spec-sig', c);
  el('b', '', sig, 'Signature: ');
  sig.append(info.ui.signature);
  const kit = el('div', 'fx-spec-kit', c);
  if (def && base) for (const k of specKit(base, def)) kitRow(kit, k.key, k.abilityId, k.baseId, o.content);
  else el('div', 'fx-spec-soon', kit, 'This path arrives soon. Its kit is being forged.');
  const foot = el('div', 'fx-spec-foot', c);
  if (!def) {
    const b = el('button', 'fx-btn ghost', foot, 'Arrives soon');
    b.type = 'button';
    b.disabled = true;
    return;
  }
  const tryB = el('button', 'fx-btn ghost', foot, 'Try (30 s)');
  tryB.type = 'button';
  tryB.title = 'Fight Warden Ilsa’s practice dummy as this specialisation for 30 seconds. Nothing is saved.';
  tryB.addEventListener('click', () => o.onTry(info.id));
  if (mine) {
    const b = el('button', 'fx-btn', foot, 'Your path');
    b.type = 'button';
    b.disabled = true;
    return;
  }
  const low = o.level < o.needLevel;
  const respec = !!o.current;
  const poor = respec && o.gold < o.respecGold;
  const choose = el('button', 'fx-btn', foot, low ? `Level ${o.needLevel}` : respec ? `Change path (${o.respecGold} gold)` : 'Choose');
  choose.type = 'button';
  choose.disabled = low || poor;
  if (poor) choose.title = `A new path costs ${o.respecGold} gold.`;
  choose.addEventListener('click', () => {
    choose.disabled = true;
    o.onChoose(info.id);
  });
}

/** The three cards (the hero's class), into `parent`. */
export function specCards(parent: HTMLElement, o: SpecCardsOpts): HTMLElement {
  const box = el('div', 'fx-spec-cards', parent);
  const base = o.content.class(o.classId);
  for (const info of SPEC_INFO[o.classId] ?? []) card(box, info, specDef(info.id), base?.specId ? undefined : base, o);
  return box;
}

/** "Breaker → Rivet Strike" rows of a spec (the celebration and its toasts). */
export function evolvedLines(content: ContentRegistry, base: ClassDef | undefined, specId: string): { from: string; to: string; abilityId: string; key: string }[] {
  const def = specDef(specId);
  if (!def || !base) return [];
  return specKit(base, def).map((k) => ({ key: k.key, abilityId: k.abilityId, from: k.baseId ? (content.ability(k.baseId)?.name ?? k.baseId) : '', to: content.ability(k.abilityId)?.name ?? k.abilityId }));
}
